import { ExportTaskRecord } from '../models/export-payload';

/**
 * Helper centralizado del Punto 8.
 *
 * Single source of truth para las columnas/campos extras que renderizan
 * los exporters CSV, XLSX y AgentJSON cuando el payload viene enriquecido
 * (`payload.extrasIncluded === true`).
 *
 * Si en el futuro añadimos un campo extra (p. ej. `tags`, `dependencies`),
 * basta con:
 *   1. Añadir el campo a ExportTaskRecord (export-payload.ts).
 *   2. Exponerlo desde exportExtrasService.js (backend).
 *   3. Añadir una entrada a este array.
 *
 * Los tres exporters lo recogen automáticamente — DRY total.
 */

export interface ExtraColumnSpec {
  /** Cabecera del CSV / XLSX. */
  header: string;
  /** Clave JS estable (snake_case, sin espacios) — útil para XLSX `column.key`. */
  key: string;
  /** Función de extracción del valor crudo. */
  pick: (t: ExportTaskRecord) => unknown;
  /** Si true, en XLSX se aplica `numFmt: '0.00'`; en CSV se serializa como número. */
  isNumeric?: boolean;
  /** Ancho recomendado para XLSX. */
  width?: number;
  /** Si true, en XLSX activa wrapText (texto largo o JSON serializado). */
  wrap?: boolean;
}

/**
 * Columnas extras para CSV y XLSX, en orden de aparición.
 * Set acordado en decisión 1(a) del Punto 8:
 *   - Todos los escalares útiles + JSONBs con valor de auditoría.
 *   - Excluido `canonical_signature` (hash opaco interno).
 */
export const EXTRA_COLUMNS_CSV_XLSX: ExtraColumnSpec[] = [
  {
    header: 'nombre',
    key: 'nombre',
    pick: t => t.name ?? '',
    width: 28,
    wrap: true
  },
  {
    header: 'subtipo_origen',
    key: 'subtipo_origen',
    pick: t => t.sourceSubtype ?? '',
    width: 18
  }
  /*{
    header: 'factor_ia',
    key: 'factor_ia',
    pick: t => t.aiAssistanceFactor ?? null,
    isNumeric: true,
    width: 12
  },
  {
    header: 'factor_calibracion',
    key: 'factor_calibracion',
    pick: t => t.calibrationFactorApplied ?? null,
    isNumeric: true,
    width: 16
  },
  {
    header: 'peso_frescura',
    key: 'peso_frescura',
    pick: t => t.freshnessWeightApplied ?? null,
    isNumeric: true,
    width: 14
  },
  {
    header: 'proyectos_analogos',
    key: 'proyectos_analogos',
    pick: t => formatJsonbForCell(t.analogProjectIds),
    width: 30,
    wrap: true
  },
  {
    header: 'especificos',
    key: 'especificos',
    pick: t => formatJsonbForCell(t.specifics),
    width: 30,
    wrap: true
  }*/
];

/**
 * Serializa un valor JSONB para una celda CSV / XLSX (decisión 3(a) del Punto 8).
 *
 * - null / undefined → cadena vacía.
 * - string → tal cual (caso poco común pero por defensa).
 * - cualquier otra cosa → JSON.stringify; fallback a String() si stringify falla
 *   por referencias circulares (no debería ocurrir, pero defensivo).
 */
export function formatJsonbForCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Conjunto de claves de campos extras que se añaden al objeto de cada task
 * en el bundle Agent JSON cuando `payload.extrasIncluded === true`.
 *
 * El Agent JSON no necesita serializar JSONBs — los mantiene como objetos
 * estructurados nativos. Por eso usa esta lista directa en lugar de
 * EXTRA_COLUMNS_CSV_XLSX.
 */
export const EXTRA_FIELDS_AGENT_JSON: Array<keyof ExportTaskRecord> = [
  'name',
  'sourceSubtype',
  /*'aiAssistanceFactor',
  'calibrationFactorApplied',
  'freshnessWeightApplied',
  'analogProjectIds',
  'specifics'*/
];

/**
 * Devuelve un objeto con SOLO los campos extras del task, útil para spread
 * en el mapTask del AgentJsonExporter.
 *
 *   { ...base, ...extractExtras(task) }
 *
 * Omite claves cuyo valor sea undefined para mantener el bundle sparse.
 */
export function extractExtras(task: ExportTaskRecord): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of EXTRA_FIELDS_AGENT_JSON) {
    const v = task[k];
    if (v !== undefined) out[k] = v;
  }
  return out;
}
